/** Pure pieces of the admin console, kept out of the components so they can be tested. */
import { day, relative } from "../lib/format";

const ACTIONS: Record<string, string> = {
  sign_in: "Signed in",
  password_changed: "Changed their password",
  authenticator_added: "Added an authenticator",
  admin_created: "Admin created (server)",
  admin_reset: "Admin reset (server)",
  business_added: "Added a business",
  switched_off: "Switched off",
  switched_on: "Switched on",
  owner_password_reset: "Reset the owner's password",
  note_changed: "Changed the note",
};

/** The audit log's action codes in words. An unknown one shows as it is. */
export function actionLabel(action: string): string {
  return ACTIONS[action] ?? action;
}

/** "JBSWY3DPEHPK3PXP" -> "JBSW Y3DP EHPK 3PXP", for typing into an authenticator by hand. */
export function groupSecret(secret: string): string {
  return secret.replace(/\s+/g, "").replace(/(.{4})(?=.)/g, "$1 ");
}

export type AdminView = "login" | "loading" | "setup" | "console";

/** What the console shows: no token is the login; a token with no answer yet is a wait. */
export function adminView(token: string | null, stage: string | null): AdminView {
  if (!token) return "login";
  if (stage === null) return "loading";
  return stage === "full" ? "console" : "setup";
}

export type SetupStep = "password" | "authenticator";

/** Password first; the server refuses the authenticator until it is done. */
export function setupStep(me: { must_change_password: boolean; totp_enabled: boolean }): SetupStep {
  return me.must_change_password ? "password" : "authenticator";
}

export const MIN_PASSWORD = 12;

/** Client-side check of the new password, before the server's own. Null when fine. */
export function passwordProblem(current: string, next: string, confirm: string): string | null {
  if (next.length < MIN_PASSWORD) return `Your new password needs at least ${MIN_PASSWORD} characters.`;
  if (next === current) return "Choose a different password from the one-time one.";
  if (next !== confirm) return "The two new passwords don't match.";
  return null;
}

/** "Today", "3 days ago", or the date once it's more than a month; "Never" for no sign-in yet. */
export function lastSeen(iso: string | null, now: number = Date.now()): string {
  if (!iso) return "Never";
  const days = (now - new Date(iso).getTime()) / 86_400_000;
  if (days < 30) return days < 1 ? "Today" : relative(iso);
  return day(iso);
}
